/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { SCAN_STEPS, type ScanStep } from "./useScan";

type Props = {
  step: ScanStep;
  /** First page, shown with a moving scan line. */
  preview: string | null;
};

// While the document is read: the page with a scan line, and the steps.
export const ScanProgress = defineComponent<Props>({
  name: "ScanProgress",
  props: ["step", "preview"],
  setup(props) {
    const { t } = useLocalization();

    return () => {
      const current = SCAN_STEPS.indexOf(props.step);
      return (
        <div class="grid grid-cols-1 items-center gap-6 sm:grid-cols-[minmax(0,14rem)_1fr]">
          <style>{`@keyframes lemmon-scan{0%{top:0}50%{top:calc(100% - 3px)}100%{top:0}}`}</style>
          <div class="relative mx-auto aspect-[3/4] w-48 overflow-hidden rounded-xl border border-base-300 bg-base-100 shadow-sm sm:w-full">
            {props.preview ? (
              <img src={props.preview} class="h-full w-full object-cover object-top opacity-90" alt="" />
            ) : (
              <div class="flex h-full items-center justify-center">
                <span class="material-symbols-outlined text-6xl text-base-content/20">description</span>
              </div>
            )}
            <div
              class="absolute inset-x-0 h-[3px] bg-primary shadow-[0_0_12px_2px] shadow-primary/60"
              style={{ animation: "lemmon-scan 2.4s ease-in-out infinite" }}
            />
          </div>
          <ol class="space-y-3">
            {SCAN_STEPS.map((s, i) => {
              const done = i < current;
              const active = i === current;
              return (
                <li key={s} class={["flex items-center gap-3", i > current ? "text-base-content/40" : ""].join(" ")}>
                  <span class="flex h-7 w-7 items-center justify-center">
                    {done ? (
                      <span class="material-symbols-outlined text-success">check_circle</span>
                    ) : active ? (
                      <span class="loading loading-spinner loading-sm text-primary" />
                    ) : (
                      <span class="material-symbols-outlined text-base-content/30">radio_button_unchecked</span>
                    )}
                  </span>
                  <div>
                    <div class={active ? "font-semibold" : ""}>{t(`inventory.scan.step_${s}`)}</div>
                    {active && s === "read" && (
                      <div class="text-xs text-base-content/50">{t("inventory.scan.step_read_hint")}</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      );
    };
  },
});
